import { useDebouncedCallback } from '@/hooks/useDebounce';
import { useCallback, useState } from 'react';

type ColumnWidths = Record<string, number>;

const DEBOUNCE_DELAY = 300;
const STORAGE_KEY = 'admin_invoice_reports_column_widths:v1';

const isColumnWidths = (value: unknown): value is ColumnWidths => {
  if (typeof value !== 'object' || value === null) return false;
  return Object.entries(value).every(
    ([key, val]) => typeof key === 'string' && typeof val === 'number'
  );
};

const loadColumnWidths = (): ColumnWidths => {
  const value = localStorage.getItem(STORAGE_KEY);
  if (!value) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    if (isColumnWidths(parsed)) return parsed;
    localStorage.removeItem(STORAGE_KEY);
    return {};
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return {};
  }
};

const saveColumnWidths = (widths: ColumnWidths): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
};

export const useResizableColumns = () => {
  const [columnWidths, setColumnWidths] =
    useState<ColumnWidths>(loadColumnWidths);

  const debouncedSave = useDebouncedCallback(saveColumnWidths, DEBOUNCE_DELAY);

  const setWidth = useCallback(
    (columnKey: string, width: number) => {
      setColumnWidths((prev) => {
        const updated = { ...prev, [columnKey]: width };
        debouncedSave(updated);
        return updated;
      });
    },
    [debouncedSave]
  );

  const handleResize = useCallback(
    (columnIndex: number, width: number, columns: Array<{ key?: unknown }>) => {
      const column = columns[columnIndex];
      if (column?.key && typeof column.key === 'string') {
        setWidth(column.key, width);
      }
    },
    [setWidth]
  );

  return { columnWidths, handleResize };
};
